import { graphql } from 'react-relay';
import { createAri } from '@atlassian/jira-platform-ari';
import type { multiSelectQuery as multiSelectQueryType } from '@atlassian/jira-relay/src/__generated__/multiSelectQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';
import type {
	MultiSelectPublicSetValue,
	PublicGetValue,
} from '../../../common/fields/multi-select';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { getValueLookupValuesDiff } from '../../../common/utils/get-value-lookup-values-diff';

type InternalValue = {
	label: string;
	value: string;
};

type MultiSelectPublicGetValue = PublicGetValue[];

// eslint-disable-next-line @atlassian/relay/must-use-inline
export const multiSelectQuery = graphql`
	query multiSelectQuery($ari: ID!, $filterById: JiraFieldOptionIdsFilterInput) {
		node(id: $ari) {
			... on JiraMultipleSelectField {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				fieldOptions(searchBy: "", filterById: $filterById) {
					edges {
						node {
							value: id
							label: value
						}
					}
				}
			}
		}
	}
`;

export const multiSelectSetValueTransformer = async (
	values: MultiSelectPublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<InternalValue[]> => {
	if (!values.length) return [];

	const optionARIs = values.map((optionId) => {
		const resourceId = `${fieldContext.fieldId}/${optionId}`;
		return createAri({
			resourceOwner: 'jira',
			cloudId: tenantContext.cloudId,
			resourceType: 'issue-field-option',
			activationId: tenantContext.activationId,
			resourceId,
		});
	});

	const response: {
		edges?: [
			{
				node?: {
					label: string | null | undefined;
					value: string;
				};
			},
		];
	} = await fetchFieldOptions<multiSelectQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs,
		nodePropertyName: 'fieldOptions',
		queryName: 'FETCH_MULTI_SELECT',
		query: multiSelectQuery,
	});

	const fieldOptions =
		response?.edges
			?.map((edge) => edge?.node)
			.filter(Boolean)
			.filter(({ value }) => optionARIs.includes(value))
			.map(({ label, value: optionValue }) => ({
				label: label ?? '',
				value: optionValue,
			})) ?? [];

	if (fieldOptions.length !== values.length) {
		const notFoundValues = getValueLookupValuesDiff(optionARIs, fieldOptions, 'value');

		throw new FieldValueLookupError(
			notFoundValues.map((value) => getResourceIdfromAri(value)).join(),
		);
	}

	return fieldOptions;
};

export const multiSelectGetValueTransformer = (
	internalValue: InternalValue[],
): MultiSelectPublicGetValue => {
	if (!internalValue) return [];

	return internalValue.map(({ value, label }) => ({
		id: getResourceIdfromAri(value),
		value: label,
	}));
};
